"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import type { CicStatement } from "@/entities/IProfile";
import { STATEMENT_GROUP, STATEMENT_TYPE } from "@/helpers/Types";
import { commonApiFetch } from "@/services/api/common-api";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useReducer } from "react";
import type { MemesSubmissionInitialDraft } from "../utils/submissionDraft";
import { createInitialState, formReducer, type CreateInitialStateInput, type ProfileDefaults } from "./artworkSubmissionFormState";
import { useArtworkSubmissionFormActions } from "./useArtworkSubmissionFormActions";
import { useArtworkSubmissionMediaControls } from "./useArtworkSubmissionMediaControls";
const getProfileBio = (statements: CicStatement[] | null | undefined): string => statements?.find(statement => statement.statement_type === STATEMENT_TYPE.BIO && statement.statement_group === STATEMENT_GROUP.GENERAL)?.statement_value ?? "";
export function useArtworkSubmissionForm(initialDraft) {
  const $ = _c(66);
  const {
    connectedProfile
  } = useAuth();
  const isDraftInitialized = Boolean(initialDraft);
  const profileHandle = connectedProfile?.handle ?? "";
  let t0;
  if ($[0] !== profileHandle) {
    t0 = profileHandle.toLowerCase();
    $[0] = profileHandle;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const normalizedProfileHandle = t0;
  const primaryWallet = connectedProfile?.primary_wallet ?? "";
  let t1;
  let t2;
  if ($[2] !== normalizedProfileHandle) {
    t1 = [QueryKey.PROFILE_CIC_STATEMENTS, normalizedProfileHandle];
    t2 = async () => await commonApiFetch({
      endpoint: `profiles/${normalizedProfileHandle}/cic/statements`
    });
    $[2] = normalizedProfileHandle;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  const t3 = !isDraftInitialized && normalizedProfileHandle.length > 0;
  let t4;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      staleTime: 60000
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    data: profileStatements
  } = useQuery(t4);
  let t5;
  if ($[9] !== profileStatements) {
    t5 = getProfileBio(profileStatements);
    $[9] = profileStatements;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const aboutArtistDefault = t5;
  let t6;
  if ($[11] !== primaryWallet || $[12] !== profileHandle) {
    t6 = {
      handle: profileHandle,
      primaryWallet
    };
    $[11] = primaryWallet;
    $[12] = profileHandle;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const profileIdentityDefaults = t6;
  let t7;
  if ($[14] !== aboutArtistDefault || $[15] !== profileIdentityDefaults) {
    t7 = {
      ...profileIdentityDefaults,
      aboutArtist: aboutArtistDefault
    };
    $[14] = aboutArtistDefault;
    $[15] = profileIdentityDefaults;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const profileDefaults = t7;
  let t8;
  if ($[17] !== initialDraft || $[18] !== profileIdentityDefaults) {
    t8 = {
      initialDraft,
      profileDefaults: profileIdentityDefaults
    };
    $[17] = initialDraft;
    $[18] = profileIdentityDefaults;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const initialStateInput = t8;
  const [state, dispatch] = useReducer(formReducer, initialStateInput, createInitialState);
  const t9 = !isDraftInitialized;
  let t10;
  if ($[20] !== profileDefaults || $[21] !== state || $[22] !== t9) {
    t10 = {
      state,
      dispatch,
      profileDefaults,
      shouldApplyProfileDefaults: t9
    };
    $[20] = profileDefaults;
    $[21] = state;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const formActions = useArtworkSubmissionFormActions(t10);
  let t11;
  if ($[24] !== state) {
    t11 = {
      state,
      dispatch
    };
    $[24] = state;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const mediaControls = useArtworkSubmissionMediaControls(t11);
  let t12;
  if ($[26] !== formActions.getMediaSelection || $[27] !== formActions.getSubmissionData || $[28] !== formActions.handleBackToArtwork || $[29] !== formActions.handleContinueFromArtwork || $[30] !== formActions.handleContinueFromTerms || $[31] !== formActions.setAboutArtist || $[32] !== formActions.setAdditionalMedia || $[33] !== formActions.setAgreements || $[34] !== formActions.setAirdropConfig || $[35] !== formActions.setAllowlistBatches || $[36] !== formActions.setCommentary || $[37] !== formActions.setPaymentInfo || $[38] !== formActions.setTraits || $[39] !== formActions.updateTraitField || $[40] !== mediaControls.clearExternalMedia || $[41] !== mediaControls.handleFileSelect || $[42] !== mediaControls.setArtworkUploaded || $[43] !== mediaControls.setExternalMediaHash || $[44] !== mediaControls.setExternalMediaMimeType || $[45] !== mediaControls.setExternalMediaProvider || $[46] !== mediaControls.setMediaSource || $[47] !== state.agreements || $[48] !== state.artworkUploaded || $[49] !== state.artworkUrl || $[50] !== state.currentStep || $[51] !== state.existingMedia || $[52] !== state.externalMedia.error || $[53] !== state.externalMedia.input || $[54] !== state.externalMedia.isValid || $[55] !== state.externalMedia.mimeType || $[56] !== state.externalMedia.previewUrl || $[57] !== state.externalMedia.provider || $[58] !== state.externalMedia.status || $[59] !== state.externalMedia.url || $[60] !== state.mediaSource || $[61] !== state.operationalData || $[62] !== state.selectedFile || $[63] !== state.traits || $[64] !== state.uploadError) {
    t12 = {
      currentStep: state.currentStep,
      agreements: state.agreements,
      setAgreements: formActions.setAgreements,
      handleContinueFromTerms: formActions.handleContinueFromTerms,
      handleContinueFromArtwork: formActions.handleContinueFromArtwork,
      handleBackToArtwork: formActions.handleBackToArtwork,
      artworkUploaded: state.artworkUploaded,
      artworkUrl: state.artworkUrl,
      uploadError: state.uploadError,
      selectedFile: state.selectedFile,
      existingMedia: state.existingMedia,
      mediaSource: state.mediaSource,
      externalMediaUrl: state.externalMedia.url,
      externalMediaPreviewUrl: state.externalMedia.previewUrl,
      externalMediaHashInput: state.externalMedia.input,
      externalMediaProvider: state.externalMedia.provider,
      externalMediaMimeType: state.externalMedia.mimeType,
      externalMediaError: state.externalMedia.error,
      externalMediaValidationStatus: state.externalMedia.status,
      isExternalMediaValid: state.externalMedia.isValid,
      setArtworkUploaded: mediaControls.setArtworkUploaded,
      setMediaSource: mediaControls.setMediaSource,
      setExternalMediaHash: mediaControls.setExternalMediaHash,
      setExternalMediaProvider: mediaControls.setExternalMediaProvider,
      setExternalMediaMimeType: mediaControls.setExternalMediaMimeType,
      clearExternalMedia: mediaControls.clearExternalMedia,
      handleFileSelect: mediaControls.handleFileSelect,
      traits: state.traits,
      setTraits: formActions.setTraits,
      updateTraitField: formActions.updateTraitField,
      operationalData: state.operationalData,
      setAirdropConfig: formActions.setAirdropConfig,
      setPaymentInfo: formActions.setPaymentInfo,
      setAllowlistBatches: formActions.setAllowlistBatches,
      setAdditionalMedia: formActions.setAdditionalMedia,
      setCommentary: formActions.setCommentary,
      setAboutArtist: formActions.setAboutArtist,
      getSubmissionData: formActions.getSubmissionData,
      getMediaSelection: formActions.getMediaSelection
    };
    $[26] = formActions.getMediaSelection;
    $[27] = formActions.getSubmissionData;
    $[28] = formActions.handleBackToArtwork;
    $[29] = formActions.handleContinueFromArtwork;
    $[30] = formActions.handleContinueFromTerms;
    $[31] = formActions.setAboutArtist;
    $[32] = formActions.setAdditionalMedia;
    $[33] = formActions.setAgreements;
    $[34] = formActions.setAirdropConfig;
    $[35] = formActions.setAllowlistBatches;
    $[36] = formActions.setCommentary;
    $[37] = formActions.setPaymentInfo;
    $[38] = formActions.setTraits;
    $[39] = formActions.updateTraitField;
    $[40] = mediaControls.clearExternalMedia;
    $[41] = mediaControls.handleFileSelect;
    $[42] = mediaControls.setArtworkUploaded;
    $[43] = mediaControls.setExternalMediaHash;
    $[44] = mediaControls.setExternalMediaMimeType;
    $[45] = mediaControls.setExternalMediaProvider;
    $[46] = mediaControls.setMediaSource;
    $[47] = state.agreements;
    $[48] = state.artworkUploaded;
    $[49] = state.artworkUrl;
    $[50] = state.currentStep;
    $[51] = state.existingMedia;
    $[52] = state.externalMedia.error;
    $[53] = state.externalMedia.input;
    $[54] = state.externalMedia.isValid;
    $[55] = state.externalMedia.mimeType;
    $[56] = state.externalMedia.previewUrl;
    $[57] = state.externalMedia.provider;
    $[58] = state.externalMedia.status;
    $[59] = state.externalMedia.url;
    $[60] = state.mediaSource;
    $[61] = state.operationalData;
    $[62] = state.selectedFile;
    $[63] = state.traits;
    $[64] = state.uploadError;
    $[65] = t12;
  } else {
    t12 = $[65];
  }
  return t12;
}
export type ArtworkSubmissionForm = ReturnType<typeof useArtworkSubmissionForm>;